import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import { brandSubscriptionsApi } from "@/resources/notification";
import { TruncatedText } from "@/components/ui/truncated-text";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddBrandDialog({ open, onOpenChange }: Props) {
  const [search, setSearch] = useState("");

  const { data, isLoading } = brandSubscriptionsApi.useGetAvailableBrandsForSubscription(open);
  const { mutate: subscribe, isPending } = brandSubscriptionsApi.useSubscribeBrand();

  const brands = data?.brands ?? [];
  const filtered = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubscribe = (brandId: number) => {
    subscribe({ brand_id: brandId }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] bg-card">
        <DialogHeader>
          <DialogTitle>Add Brand</DialogTitle>
          <DialogDescription>
            Subscribe to a brand to receive notifications for it.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search brands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 rounded-lg bg-card"
          />
        </div>

        <div className="border border-border rounded-lg max-h-[280px] overflow-y-auto divide-y divide-border">
          {isLoading && (
            <>
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 px-3 py-2.5">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-7 w-20" />
                </div>
              ))}
            </>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No brands available</p>
            </div>
          )}

          {!isLoading &&
            filtered.map((brand) => (
              <div
                key={brand.id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <div className="h-8 w-8 rounded-full bg-primary-tint-soft flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                  {brand.name.charAt(0).toUpperCase()}
                </div>
                <span className="flex-1 min-w-0 text-sm font-medium text-foreground">
                  <TruncatedText>{brand.name}</TruncatedText>
                </span>
                <Button
                  size="sm"
                  className="h-7 text-xs rounded-lg"
                  disabled={isPending}
                  onClick={() => handleSubscribe(brand.id)}
                >
                  Subscribe
                </Button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
